<template>
  <div id="app" class="h-full">
    <Onboarding
      v-if="!$apollo.loading && showOnboarding && !offerPresent"
      :user-email="account.email"
      :brands="account.brands"
    />

    <notifications
      v-if="!$apollo.loading && !showOnboarding && !offerPresent"
      group="general"
      position="top center"
      classes="promo-notification"
      width="400px"
    />

    <div
      v-if="$apollo.loading || offerPresent"
      class="absolute w-screen h-screen"
    >
      <Loader class="v-center" :label="loaderLabel" />
    </div>

    <component
      :is="this.$route.meta.layout || 'div'"
      v-else-if="!$apollo.loading && !showOnboarding"
    >
      <router-view />
    </component>

    <Modals
      :store-name="account && account.name"
      @login-success="onLoginSuccess"
    />
  </div>
</template>

<script>
import LogRocket from "logrocket";
import isAbsoluteUrl from "is-absolute-url";
import OnboardingLoadError from "@/components/Onboarding/OnboardingLoadError";
import Loader from "@/components/UI/Loader";
import typography from "@/utils/typography";
import Modals from "@/components/Modals";
import {
  getAccountId,
  isAuthenticated,
  setupSession,
  unauthenticate,
} from "@/lib/auth";
import { Account } from "@/graphql/queries";
import { isProduction } from "@/utils/constants";
import EventBus from "@/lib/event-bus.js";

const Onboarding = () => ({
  // The component to load (should be a Promise)
  component: import(
    /* webpackChunkName: "Onboarding" */ "@/components/Onboarding/index"
  ),
  // A component to use while the async component is loading
  loading: Loader,
  // A component to use if the load fails
  error: OnboardingLoadError,
  // Delay before showing the loading component. Default: 200ms.
  delay: 200,
  // The error component will be displayed if a timeout is
  // provided and exceeded. Default: Infinity.
  timeout: 10000,
});

// Screen sizes from tailwind config
// We can't import these automatically because Edge/IE no likey
const screens = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
};

export default {
  apollo: {
    account: {
      query: Account,
      update: (data) => data.account,
      skip: true,
      result({ loading }) {
        // If we've finished loading...
        if (!loading) this.setupAccount();
      },
    },
  },
  name: "App",
  components: {
    Loader,
    Modals,
    Onboarding,
  },
  provide: {
    appType: "cli",
    DEVELOPMENT: !isProduction,
    screens,
  },
  data() {
    return {
      account: null,
      offerPresent: false,
    };
  },
  computed: {
    loaderLabel() {
      return this.offerPresent ? "Loading Offer..." : "Loading...";
    },
    offerCode() {
      // Check the route for an offer...
      let offerCode = this.$has(this.$route, "query.offer")
        ? this.$route.query.offer
        : null;

      // If the route doesn't have an offerCode check localStorage...
      offerCode = offerCode || window.localStorage.getItem("pai-offerCode");

      return offerCode;
    },
    showOnboarding() {
      return (
        this.$has(this.account, "onboardingComplete") &&
        !this.account.onboardingComplete
      );
    },
  },
  metaInfo: {
    // if no subcomponents specify a metaInfo.title, this title will be used
    title: "Newsletters made easy",
    // all titles will be injected into this template
    titleTemplate: "Promo.ai | %s",
    link: [
      {
        rel: "stylesheet",
        href:
          "https://fonts.googleapis.com/css?family=Lato:900|Open+Sans:300,400,600",
      },
    ],
    style: [{ cssText: typography, type: "text/css" }],
  },
  mounted() {
    setupSession();

    this.setupEventBus();

    // Init the app setup if we're already logged in...
    this.initAppSetup();
  },
  beforeDestroy() {
    this.removeEventBus();
  },
  methods: {
    initAppSetup() {
      // If the user is logged in...
      if (isAuthenticated()) {
        // If we have an offerCode then run the setup...
        if (this.offerCode) {
          this.setupOffers();
        } else {
          // Otherwise start the account query...
          this.$apollo.queries.account.start();

          this.$gtm.trackEvent({
            event: "promo-initial-load",
            stage: process.env.VUE_APP_ENV,
            accountId: getAccountId(),
          });
        }
      }
    },
    handleRedirects() {
      // Get any redirect query from the route or localStorage...
      const redirect =
        this.$route?.query?.redirect ||
        window.localStorage.getItem("pai-redirect");

      // Clear localStorage so we don't use the redirect next time around...
      window.localStorage.removeItem("pai-redirect");

      if (redirect) {
        isAbsoluteUrl(redirect)
          ? // If we have a external redirect then lets go there...
            (window.document.location.href = redirect)
          : // Otherwise push to the internal redirect after the DOM updates...
            this.$nextTick(() => this.$router.push(redirect));
      }
    },
    onLoginSuccess() {
      // Init the app setup if we've just logged in...
      this.initAppSetup();
    },
    onLogout() {
      unauthenticate();
    },
    onNetworkError() {
      this.$notify({
        group: "general",
        duration: 8000,
        type: "warn",
        title: "🙊 Network issues detected...",
        text:
          "It looks like you might be experiencing issues with your internet connection. Please check your connection and try again.",
      });
    },
    removeEventBus() {
      EventBus.$off("network-error");
    },
    setupEventBus() {
      EventBus.$on("network-error", () => this.onNetworkError());
    },
    setupAccount() {
      this.setupLogRocket();

      // Redirect if the route has a redirect querystring
      this.handleRedirects();
    },
    setupLogRocket() {
      // Ignore if we're on the unsubscribed page
      if (window?.location?.pathname === "/unsubscribed") return false;

      // Only setup LogRocket in production and as long as we have an accountId
      if (isProduction && this.account?.accountId) {
        const { accountId, name, email } = this.account;

        LogRocket.identify(accountId, {
          name: name,
          email: email,
          // Add your own custom user variables here, ie:
          // subscriptionType: 'pro'
        });
      }
    },
    setupOffers() {
      this.offerPresent = true;

      // Clear localStorage...
      window.localStorage.removeItem("pai-offerCode");

      const offerUrl = `${
        process.env.VUE_APP_SERVICES_URL
      }/package/change/${getAccountId()}/${this.offerCode}`;

      // Redirect the user to the offer page...
      window.document.location.href = offerUrl;
    },
  },
};
</script>
